import React, { useEffect, useState } from "react";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "levami_cookie_consent_v1";

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { t } = useLocale();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setVisible(false);
    window.dispatchEvent(new CustomEvent("levami-consent-updated", { detail: consent }));
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[70] flex justify-center px-3 pb-3 sm:pb-5"
    >
      <div className="w-full max-w-2xl bg-levami-creme border border-levami-sable rounded-t-xl sm:rounded-xl shadow-[var(--shadow-float)] p-5 sm:p-6">
        <h2 className="font-serif-heading text-lg text-levami-charbon mb-2">{t("cookie_consent.title")}</h2>
        <p className="text-sm text-levami-brun/80 leading-relaxed mb-4">
          {t("cookie_consent.text")}{" "}
          <a href="/cookies" className="underline hover:text-levami-primary">{t("cookie_consent.policy_link")}</a>.
        </p>

        {customizing && (
          <div className="space-y-3 mb-4 border-t border-levami-sable pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-levami-charbon">{t("cookie_consent.essential")}</span>
              <Switch checked disabled data-testid="cookie-toggle-essential" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-levami-charbon">{t("cookie_consent.analytics")}</span>
              <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-toggle-analytics" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-levami-charbon">{t("cookie_consent.marketing")}</span>
              <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="cookie-toggle-marketing" />
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            data-testid="cookie-accept-all-button"
            onClick={() => save({ essential: true, analytics: true, marketing: true, date: new Date().toISOString() })}
            className="flex-1 bg-levami-charbon text-levami-creme hover:bg-levami-brun"
          >
            {t("cookie_consent.accept_all")}
          </Button>
          <Button
            data-testid="cookie-reject-all-button"
            onClick={() => save({ essential: true, analytics: false, marketing: false, date: new Date().toISOString() })}
            variant="outline"
            className="flex-1 border-levami-charbon text-levami-charbon hover:bg-levami-ivoire"
          >
            {t("cookie_consent.reject_all")}
          </Button>
          {!customizing ? (
            <Button
              data-testid="cookie-customize-button"
              onClick={() => setCustomizing(true)}
              variant="ghost"
              className="flex-1 text-levami-charbon hover:bg-levami-ivoire"
            >
              {t("cookie_consent.customize")}
            </Button>
          ) : (
            <Button
              data-testid="cookie-save-preferences-button"
              onClick={() => save({ essential: true, analytics, marketing, date: new Date().toISOString() })}
              variant="ghost"
              className="flex-1 text-levami-charbon hover:bg-levami-ivoire"
            >
              {t("cookie_consent.save_preferences")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
